/**
 * useTicker — subscribes to live tick SSE stream from /api/ticks/stream.
 * Falls back to REST snapshot polling every 15s while SSE is down, and retries SSE with backoff.
 *
 * Returns: { ticks: {SYMBOL: {ltp, open, high, low, close, volume, change_pct, source, age_sec, is_live, _rx}},
 *            connected, mode }
 * (`_rx` = client receipt time in ms; see lib/priceLabel.js)
 *
 * RU-SIGUI2 (SU2-8/SU2-9): `mode` is "stream" (SSE open), "polling" (SSE down, the last REST snapshot
 * succeeded) or "offline" (neither). `connected` is true in "stream" and "polling", so the per-tick
 * is_live / age labels decide LIVE vs DELAYED in both; only "offline" is not connected. After an SSE error
 * the stream is retried after SSE_RETRY_BASE_MS, doubling up to SSE_RETRY_MAX_MS, and at once when the page
 * becomes visible again (a phone backgrounding the app drops the stream).
 */
import { useEffect, useRef, useState } from "react";
import { API_BASE } from "@/lib/api";

export const POLL_INTERVAL_MS = 15000;
export const SSE_RETRY_BASE_MS = 2000;
export const SSE_RETRY_MAX_MS = 60000;

export function useTicker(symbols = []) {
  const [ticks, setTicks] = useState({});
  const [mode, setMode]   = useState("offline");
  const esRef             = useRef(null);
  const pollRef           = useRef(null);

  const symbolParam = symbols.length > 0 ? symbols.join(",") : "";

  useEffect(() => {
    let dead = false;
    let streamOpen = false;
    let snapshotOk = false;
    let retryTimer = null;
    let retryDelay = SSE_RETRY_BASE_MS;
    const qs = symbolParam ? `?symbols=${encodeURIComponent(symbolParam)}` : "";

    function stopPolling() {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    }

    function startPolling() {
      if (pollRef.current || dead) return;
      fetchSnapshot();
      pollRef.current = setInterval(fetchSnapshot, POLL_INTERVAL_MS);
    }

    function scheduleRetry() {
      if (dead || retryTimer) return;
      const delay = retryDelay;
      retryDelay = Math.min(retryDelay * 2, SSE_RETRY_MAX_MS);
      retryTimer = setTimeout(() => { retryTimer = null; startSSE(); }, delay);
    }

    function startSSE() {
      if (dead) return;
      if (esRef.current) esRef.current.close();
      let es;
      try {
        es = new EventSource(`${API_BASE}/ticks/stream${qs}`, { withCredentials: true });
      } catch {
        esRef.current = null;
        startPolling();
        scheduleRetry();
        return;
      }
      esRef.current = es;

      es.onopen = () => {
        if (dead || esRef.current !== es) return;
        streamOpen = true;
        retryDelay = SSE_RETRY_BASE_MS;
        stopPolling();
        setMode("stream");
      };

      es.onmessage = (evt) => {
        if (dead) return;
        try {
          const tick = JSON.parse(evt.data);
          if (tick.symbol) {
            setTicks((prev) => ({ ...prev, [tick.symbol]: { ...tick, _rx: Date.now() } }));
          }
        } catch {}
      };

      es.onerror = () => {
        es.close();
        if (dead || esRef.current !== es) return;
        esRef.current = null;
        streamOpen = false;
        setMode(snapshotOk ? "polling" : "offline");
        // Fall back to REST polling, and try the stream again later
        startPolling();
        scheduleRetry();
      };
    }

    async function fetchSnapshot() {
      if (dead) return;
      let ok = false;
      try {
        const r = await fetch(`${API_BASE}/ticks/snapshot${qs}`, { credentials: "include" });
        if (r.ok) {
          const json = await r.json();
          ok = true;
          if (json.ticks && !dead) {
            const rx = Date.now();   // RU-M4-H2 ML-7: receipt time, so a tick's age keeps growing client-side
            const stamped = {};
            for (const [k, v] of Object.entries(json.ticks)) stamped[k] = { ...v, _rx: rx };
            setTicks((prev) => ({ ...prev, ...stamped }));
          }
        }
      } catch {}
      if (dead) return;
      snapshotOk = ok;
      if (!streamOpen) setMode(ok ? "polling" : "offline");
    }

    function onVisible() {
      if (dead || document.hidden || streamOpen) return;
      if (esRef.current && esRef.current.readyState === 0) return;   // a stream is already connecting
      // back from the background: retry the stream now (and refresh prices meanwhile)
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      retryDelay = SSE_RETRY_BASE_MS;
      fetchSnapshot();
      startSSE();
    }

    // Try SSE first; polling starts automatically on SSE error
    startSSE();

    // Also do an immediate REST snapshot so UI isn't empty on first load
    fetchSnapshot();

    document.addEventListener("visibilitychange", onVisible);

    return () => {
      dead = true;
      document.removeEventListener("visibilitychange", onVisible);
      esRef.current?.close();
      esRef.current = null;
      stopPolling();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [symbolParam]);

  return { ticks, connected: mode !== "offline", mode };
}
